import { useSyncExternalStore, useCallback } from 'react';

export type SortField = 'created_at' | 'title' | 'reading_time';
export type SortOrder = 'asc' | 'desc';

export interface SortPreference {
  field: SortField;
  order: SortOrder;
}

const STORAGE_KEY = 'readlater-sort-preference';

const DEFAULT_PREFERENCE: SortPreference = {
  field: 'created_at',
  order: 'desc',
};

// Module-level shared state
const listeners = new Set<() => void>();

let currentPreference: SortPreference = (() => {
  if (typeof window === 'undefined') return DEFAULT_PREFERENCE;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (
        ['created_at', 'title', 'reading_time'].includes(parsed.field) &&
        ['asc', 'desc'].includes(parsed.order)
      ) {
        return parsed;
      }
    }
  } catch {
    // Invalid JSON or localStorage error
  }
  return DEFAULT_PREFERENCE;
})();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return currentPreference;
}

function setPreference(next: SortPreference) {
  currentPreference = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage might be full or disabled
  }
  listeners.forEach((l) => l());
}

export function useSortPreference() {
  const sortPreference = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULT_PREFERENCE);

  const setSortField = useCallback((field: SortField) => {
    const prev = currentPreference;
    if (prev.field === field) {
      setPreference({ ...prev, order: prev.order === 'asc' ? 'desc' : 'asc' });
    } else {
      const defaultOrder: SortOrder = field === 'title' ? 'asc' : 'desc';
      setPreference({ field, order: defaultOrder });
    }
  }, []);

  const setSortOrder = useCallback((order: SortOrder) => {
    setPreference({ ...currentPreference, order });
  }, []);

  const setSortPreference = useCallback((pref: SortPreference) => {
    setPreference(pref);
  }, []);

  return {
    sortPreference,
    setSortField,
    setSortOrder,
    setSortPreference,
  };
}
